import { Fragment, useState, useEffect } from "react";
import { CheckIcon } from "lucide-react";
import { HiOutlineX } from "react-icons/hi";
import pdfIcon from "../../assets/svg/pdf-icon.svg";
import DateTimePicker from "../../components/DateTimePicker";
import {
  getUserAffidavitContext,
  updateUserAffidavitEntry,
  generateUserAffidavitPdf,
  type UserAffidavitItem,
} from "../../api/discussion";
import { toast } from "sonner";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import { toLocalYmd, parseFilingApiDateString } from "../../utils/filingApiDates";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function formatDateOrdinal(d: Date): string {
  const s = ["th", "st", "nd", "rd"];
  const v = d.getDate();
  return `${v}${s[(v % 10 > 3 || (v >= 10 && v < 20)) ? 0 : v % 10]} ${d.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  })}`;
}

const LABEL_STYLE =
  "text-xs font-normal leading-5 text-[#222] flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

function markKey(classNumber: number, typeOfMark: string): string {
  return `${classNumber}::${typeOfMark}`;
}

function formatDocDateDdMmYyyy(s: string | null | undefined): string {
  if (!s) return "—";
  const trimmed = String(s).trim();
  // Fast path: YYYY-MM-DD or YYYY-MM-DDTHH:MM... — just slice and rearrange.
  const ymd = trimmed.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return `${ymd.slice(8, 10)}-${ymd.slice(5, 7)}-${ymd.slice(0, 4)}`;
  }
  // Already DD-MM-YYYY — leave as-is.
  if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return trimmed;
  // Fallback: parse and reformat.
  const d = parseFilingApiDateString(trimmed);
  if (!d) return trimmed;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

interface PreparationOfUserAffidavitViewProps {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSkipToNextStep?: () => void;
}

export default function PreparationOfUserAffidavitView({
  leadId,
  onSuccess,
  onSkipToNextStep,
}: PreparationOfUserAffidavitViewProps) {
  const [userAffidavits, setUserAffidavits] = useState<UserAffidavitItem[]>([]);
  const [isStepActive, setIsStepActive] = useState(false);
  const [selectedMarkKey, setSelectedMarkKey] = useState("");
  const [dateOfUserAffidavit, setDateOfUserAffidavit] = useState<Date | null>(null);
  const [isRequired, setIsRequired] = useState(true);

  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const isDateMissing = !dateOfUserAffidavit;

  const validateRequiredFields = (): boolean => {
    if (isDateMissing) {
      setShowValidationErrors(true);
      toast.error("Date of User Affidavit is required.");
      return false;
    }
    return true;
  };

  const selectedEntry = userAffidavits.find(
    (u) => markKey(u.classNumber, u.typeOfMark) === selectedMarkKey
  );

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getUserAffidavitContext(leadId)
      .then((res) => {
        if (cancelled) return;
        setIsStepActive(res.isStepActive);
        const items = res.userAffidavits ?? [];
        setUserAffidavits(items);
        if (items.length > 0) {
          const first = items[0];
          const key = markKey(first.classNumber, first.typeOfMark);
          setSelectedMarkKey(key);
          setIsRequired(first.isRequired);
          setDateOfUserAffidavit(
            first.dateOfUserAffidavit
              ? new Date(first.dateOfUserAffidavit)
              : null
          );
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load User Affidavit data"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // When switching applications: persist current form values into userAffidavits
  // so unsaved dates are not lost, then load the newly selected entry
  useEffect(() => {
    if (!selectedMarkKey || userAffidavits.length === 0) return;
    const entry = userAffidavits.find(
      (u) => markKey(u.classNumber, u.typeOfMark) === selectedMarkKey
    );
    if (!entry) return;
    setIsRequired(entry.isRequired);
    setDateOfUserAffidavit(
      entry.dateOfUserAffidavit ? new Date(entry.dateOfUserAffidavit) : null
    );
  }, [selectedMarkKey, userAffidavits]);

  // Persist current form values into userAffidavits before switching, so unsaved edits are kept
  const handleApplicationSelect = (key: string) => {
    if (selectedMarkKey) {
      const prev = userAffidavits.find(
        (u) => markKey(u.classNumber, u.typeOfMark) === selectedMarkKey
      );
      if (prev) {
        const dateIso = dateOfUserAffidavit ? toLocalYmd(dateOfUserAffidavit) : null;
        setUserAffidavits((prevItems) =>
          prevItems.map((u) =>
            markKey(u.classNumber, u.typeOfMark) === selectedMarkKey
              ? {
                  ...u,
                  isRequired,
                  dateOfUserAffidavit: dateIso,
                  dateOfUserAffidavitFormatted: dateIso
                    ? formatDateOrdinal(new Date(dateIso))
                    : null,
                }
              : u
          )
        );
      }
    }
    setSelectedMarkKey(key);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (!validateRequiredFields()) return;
    setSaving(true);
    try {
      // Merge current form into userAffidavits before saving
      const merged = userAffidavits.map((u) => {
        const key = markKey(u.classNumber, u.typeOfMark);
        if (key === selectedMarkKey) {
          const dateIso = dateOfUserAffidavit ? toLocalYmd(dateOfUserAffidavit) : null;
          return {
            ...u,
            isRequired,
            dateOfUserAffidavit: dateIso,
            dateOfUserAffidavitFormatted: dateIso
              ? formatDateOrdinal(new Date(dateIso))
              : null,
          };
        }
        return u;
      });
      setUserAffidavits(merged);

      // Save all applications that have dateOfUserAffidavit
      for (const item of merged) {
        if (item.dateOfUserAffidavit) {
          await updateUserAffidavitEntry(leadId, {
            classNumber: item.classNumber,
            typeOfMark: item.typeOfMark,
            isRequired: item.isRequired,
            dateOfUserAffidavit: item.dateOfUserAffidavit,
          });
        }
      }
      const res = await getUserAffidavitContext(leadId);
      setUserAffidavits(res.userAffidavits ?? []);
      const updated = res.userAffidavits?.find(
        (u) => markKey(u.classNumber, u.typeOfMark) === selectedMarkKey
      );
      if (updated) {
        setIsRequired(updated.isRequired);
        setDateOfUserAffidavit(
          updated.dateOfUserAffidavit
            ? new Date(updated.dateOfUserAffidavit)
            : null
        );
      }
      toast.success("User affidavit saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId || !selectedEntry) return;
    setGeneratingPdf(true);
    try {
      // Persist latest data for the selected entry before generating,
      // so the backend has the date when it builds the PDF.
      const dateIso = dateOfUserAffidavit ? toLocalYmd(dateOfUserAffidavit) : null;
      if (dateIso) {
        await updateUserAffidavitEntry(leadId, {
          classNumber: selectedEntry.classNumber,
          typeOfMark: selectedEntry.typeOfMark,
          isRequired,
          dateOfUserAffidavit: dateIso,
        });
      }

      const res = await generateUserAffidavitPdf(leadId, {
        classNumber: selectedEntry.classNumber,
        typeOfMark: selectedEntry.typeOfMark,
      });
      setUserAffidavits((prev) =>
        prev.map((u) =>
          u.classNumber === res.classNumber && u.typeOfMark === res.typeOfMark
            ? { ...u, pdf: { key: res.key, url: res.url, filename: res.filename } }
            : u
        )
      );
      const fresh = await getUserAffidavitContext(leadId);
      setUserAffidavits(fresh.userAffidavits ?? []);
      toast.success("User affidavit PDF generated successfully.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownload = async (): Promise<void> => {
    const pdf = selectedEntry?.pdf;
    if (!pdf?.url) {
      toast.error("Generate PDF first before downloading.");
      return;
    }
    setDownloadingPdf(true);
    try {
      const result = await downloadFileFromUrlWithFallback(
        pdf.url,
        pdf.filename ?? "user-affidavit.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePreview = async () => {
    if (!leadId || !selectedEntry) return;
    if (!dateOfUserAffidavit || !isRequired) {
      toast.error("Please complete required fields before preview.");
      return;
    }
    setPreviewPdfUrl(null);
    setNumPages(null);
    if (selectedEntry.pdf?.url) {
      setPreviewPdfUrl(selectedEntry.pdf.url);
      return;
    }
    setPreviewing(true);
    try {
      // Persist latest data for the selected entry before generating preview
      const dateIso = dateOfUserAffidavit ? toLocalYmd(dateOfUserAffidavit) : null;
      if (dateIso) {
        await updateUserAffidavitEntry(leadId, {
          classNumber: selectedEntry.classNumber,
          typeOfMark: selectedEntry.typeOfMark,
          isRequired,
          dateOfUserAffidavit: dateIso,
        });
      }

      const res = await generateUserAffidavitPdf(leadId, {
        classNumber: selectedEntry.classNumber,
        typeOfMark: selectedEntry.typeOfMark,
      });
      setUserAffidavits((prev) =>
        prev.map((u) =>
          u.classNumber === res.classNumber && u.typeOfMark === res.typeOfMark
            ? { ...u, pdf: { key: res.key, url: res.url, filename: res.filename } }
            : u
        )
      );
      if (res.url) {
        setPreviewPdfUrl(res.url);
      } else {
        toast.error("Failed to generate PDF for preview.");
      }
      const fresh = await getUserAffidavitContext(leadId);
      setUserAffidavits(fresh.userAffidavits ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Loading User Affidavit data...
        </p>
      </div>
    );
  }

  if (!isStepActive) {
    return (
      <div
        className="flex flex-col items-center w-full"
        style={{
          fontFamily: "Inter, sans-serif",
          gap: 22,
          fontFeatureSettings: "'ss01' on, 'cv01' on",
        }}
      >
        <div
          className="flex flex-col items-center w-full max-w-[1085px]"
          style={{ gap: 20 }}
        >
          <div
            className="flex items-center text-[#222222]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "20px",
              fontFeatureSettings: "'ss01' on, 'cv01' on",
            }}
          >
            Not Required
          </div>
          <div
            className="flex flex-row justify-center items-center"
            style={{ gap: 20 }}
          >
            <div
              className="flex flex-col justify-center items-center"
              style={{ gap: 20, width: 265, minHeight: 94 }}
            >
              <div
                className="flex items-center text-center text-[#222222] max-w-[267px]"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: "20px",
                  fontFeatureSettings: "'ss01' on, 'cv01' on",
                }}
              >
                The User Date is set to &quot;Proposed to be used&quot;. User
                Affidavit is not required.
              </div>
              <button
                type="button"
                onClick={onSkipToNextStep}
                className="box-border flex flex-row justify-center items-center gap-2.5 cursor-pointer hover:bg-[#EEEEEE]/50 transition-colors min-h-[44px] md:min-h-[36px] min-w-[145px] px-2.5 py-2 bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px] touch-manipulation"
              >
                <span
                  className="flex items-center text-[#6D6D6D]"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: "20px",
                    fontFeatureSettings: "'ss01' on, 'cv01' on",
                  }}
                >
                  Skip to Next Step
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (userAffidavits.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No marks require User Affidavit. Please complete the confirmation
          step first.
        </p>
      </div>
    );
  }

  const detailsRows = selectedEntry
    ? [
        { label: "Mark", value: selectedEntry.mark ?? "—" },
        { label: "Applicant", value: selectedEntry.applicant ?? "—" },
        {
          label: "Applicant Address",
          value: selectedEntry.applicantAddress ?? "—",
        },
        { label: "Applicant Type", value: selectedEntry.applicantType ?? "—" },
        {
          label: "Description of Applicant",
          value: selectedEntry.descriptionOfApplicant ?? "—",
        },
        {
          label: "No of Applications",
          value:
            selectedEntry.noOfApplications != null
              ? String(selectedEntry.noOfApplications)
              : "—",
        },
        {
          label: "Signatory Name",
          value: selectedEntry.signatoryName ?? "—",
        },
        {
          label: "Signatory's Gender",
          value: selectedEntry.signatoryGender ?? "—",
        },
        {
          label: "Signatory Relation with Guardian",
          value: selectedEntry.signatoryRelationWithGuardian ?? "—",
        },
        {
          label: "Signatory Guardian Name",
          value: selectedEntry.signatoryGuardianName ?? "—",
        },
        {
          label: "Signatory Age",
          value: selectedEntry.signatoryAge ?? "—",
        },
        {
          label: "Signatory Designation",
          value: selectedEntry.signatoryDesignation ?? "—",
        },
        {
          label: "Signatory Details",
          value: selectedEntry.signatoryDetails ?? "—",
        },
        {
          label: "User Date",
          value: selectedEntry.userDateFormatted ?? selectedEntry.userDate ?? "—",
        },
        {
          label: "Date of User Affidavit",
          value: dateOfUserAffidavit
            ? formatDateOrdinal(dateOfUserAffidavit)
            : selectedEntry.dateOfUserAffidavitFormatted ?? "—",
        },
        {
          label: `Goods / Services (Class ${selectedEntry.classNumber})`,
          value: selectedEntry.goodsServices || "—",
        },
        {
          label: "GS Type A",
          value: selectedEntry.gsTypeA || "—",
        },
        {
          label: "GS Type B",
          value: selectedEntry.gsTypeB || "—",
        },
        {
          label: "GS Type C",
          value: selectedEntry.gsTypeC || "—",
        },
      ]
    : [];

  const supportingDocs = selectedEntry?.supportingDocuments ?? [];

  return (
    <form
      id="preparation-of-user-affidavit-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row md:items-start w-full gap-4 md:gap-5">
        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <label className={LABEL_STYLE} style={LABEL_FEATURES}>
            <span className="min-w-0 break-words">
              Date of User Affidavit
              <span className="text-[#E7000B]"> *</span>
            </span>
          </label>
          <DateTimePicker
            value={dateOfUserAffidavit}
            onChange={(d) => {
              setDateOfUserAffidavit(d);
              if (d) setShowValidationErrors(false);
            }}
            showTime={false}
            buttonClassName={`min-h-[44px] md:min-h-0 rounded-[6px] text-[12px] md:text-xs ${
              showValidationErrors && isDateMissing
                ? "border-[#E7000B] focus:ring-[#E7000B]"
                : ""
            }`}
          />
          {showValidationErrors && isDateMissing && (
            <p className="text-[11px] text-[#E7000B]">
              Date of User Affidavit is required.
            </p>
          )}
        </div>
      </div>

      {/* Application tabs */}
      <div className="flex flex-wrap gap-2.5 md:gap-5 items-stretch md:items-center">
        {userAffidavits.map((ua, idx) => {
          const key = markKey(ua.classNumber, ua.typeOfMark);
          const isSelected = key === selectedMarkKey;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleApplicationSelect(key)}
              className={`flex items-center justify-center gap-2.5 px-3 py-2.5 min-h-[44px] md:min-h-0 md:py-2 rounded-[6px] border text-[12px] md:text-xs font-normal transition-colors touch-manipulation min-w-0 flex-1 sm:flex-initial sm:min-w-[150px] ${
                isSelected
                  ? "bg-[#222] border-[#222] text-white"
                  : "bg-[#fcfcfc] border-[#eee] text-[#6d6d6d] hover:bg-[#f7f7f7]"
              }`}
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <CheckIcon className="size-4 shrink-0" strokeWidth={2} />
              <span>Application {idx + 1}</span>
            </button>
          );
        })}
      </div>

      {/* Read-only details — compact invoice-style preview grid */}
      <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 w-full rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5">
        {detailsRows.map((row) => {
          const isEmpty = !row.value || row.value === "—";
          return (
            <Fragment key={row.label}>
              <div className="flex items-baseline font-medium text-[#222]">
                {row.label}
              </div>
              <div className="flex items-baseline text-[#222]">:</div>
              <div className="text-[#222] break-words min-w-0">
                {isEmpty ? (
                  <span className="text-[#6D6D6D]">—</span>
                ) : (
                  row.value
                )}
              </div>
            </Fragment>
          );
        })}
      </div>

      {/* List of Supporting Documents - table on desktop, cards on mobile */}
      <div className="flex flex-col gap-4">
        <h3
          className="text-xs sm:text-sm font-medium text-[#101828] pb-2.5 border-b-2 border-[#1d293d]"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          List of Supporting Documents :
        </h3>
        <div className="border border-[#d1d5dc] rounded overflow-hidden">
          <table className="w-full text-[12px] md:text-xs hidden md:table" style={{ fontFamily: "Inter, sans-serif" }}>
            <thead>
              <tr className="bg-[#eee]">
                <th className="border border-[#d1d5dc] text-left font-bold text-[#242424] p-3 w-[64px]">
                  #
                </th>
                <th className="border border-[#d1d5dc] text-left font-bold text-[#242424] p-3 min-w-[189px]">
                  Document Name
                </th>
                <th className="border border-[#d1d5dc] text-left font-bold text-[#242424] p-3">
                  Issued By
                </th>
                <th className="border border-[#d1d5dc] text-left font-bold text-[#242424] p-3 min-w-[185px]">
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {supportingDocs.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="border border-[#d1d5dc] p-4 text-[#6d6d6d] text-center"
                  >
                    No supporting documents
                  </td>
                </tr>
              ) : (
                supportingDocs.map((doc) => (
                  <tr key={doc.docNo} className="border-b border-[#d1d5dc] last:border-b-0">
                    <td className="border border-[#d1d5dc] p-3 font-semibold text-[#0a0a0a]">
                      {doc.docNo}
                    </td>
                    <td className="border border-[#d1d5dc] p-3 text-[#0a0a0a]">
                      {doc.docName}
                    </td>
                    <td className="border border-[#d1d5dc] p-3 text-[#0a0a0a]">
                      {doc.docDetail}
                    </td>
                    <td className="border border-[#d1d5dc] p-3 text-[#0a0a0a]">
                      {formatDocDateDdMmYyyy(doc.docDate)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {/* Mobile: card per document */}
          <div className="md:hidden flex flex-col">
            {supportingDocs.length === 0 ? (
              <div className="p-4 text-[#6d6d6d] text-center text-[12px]">
                No supporting documents
              </div>
            ) : (
              supportingDocs.map((doc) => (
                <div
                  key={doc.docNo}
                  className="flex flex-col gap-1.5 p-3 border-b border-[#d1d5dc] last:border-b-0 bg-white"
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[12px] font-normal text-[#6d6d6d]">#</span>
                    <span className="text-[12px] font-semibold text-[#0a0a0a]">{doc.docNo}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[12px] font-normal text-[#6d6d6d]">Document Name</span>
                    <span className="text-[12px] text-[#0a0a0a]">{doc.docName}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[12px] font-normal text-[#6d6d6d]">Issued By</span>
                    <span className="text-[12px] text-[#0a0a0a]">{doc.docDetail}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[12px] font-normal text-[#6d6d6d]">Date</span>
                    <span className="text-[12px] text-[#0a0a0a]">{formatDocDateDdMmYyyy(doc.docDate)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <button
          type="button"
          onClick={handleGeneratePdf}
          disabled={
            generatingPdf ||
            !dateOfUserAffidavit ||
            !isRequired ||
            !selectedEntry
          }
          className="flex min-h-[44px] sm:h-9 items-center justify-center gap-2 rounded-[6px] border border-[#eee] bg-[#fcfcfc] pl-4 pr-2 py-2.5 sm:py-1 text-[12px] md:text-xs font-medium leading-5 text-[#222] hover:bg-[#f7f7f7] disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <img src={pdfIcon} alt="" className="h-[22px] w-[22px] shrink-0" />
          {generatingPdf ? "Generating..." : "Generate PDF"}
        </button>
        <button
          type="button"
          onClick={() => {
            void handleDownload();
          }}
          disabled={downloadingPdf || !selectedEntry?.pdf?.url}
          className="min-h-[44px] sm:h-9 rounded-[6px] bg-[#222] px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-white hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {downloadingPdf ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={
            previewing ||
            !dateOfUserAffidavit ||
            !isRequired ||
            !selectedEntry
          }
          className="min-h-[44px] sm:h-9 rounded-[6px] border border-[#eee] bg-[#fcfcfc] px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-[#222] hover:bg-[#f7f7f7] disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {previewing ? "Generating..." : "Preview"}
        </button>
      </div>


      {/* PDF Preview modal - mobile friendly */}
      {previewPdfUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
          <div className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl overflow-hidden">
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 sm:px-4 py-3">
              <span className="text-xs sm:text-sm font-medium text-[#222] truncate pr-2" style={{ fontFamily: "Inter, sans-serif" }}>
                User Affidavit PDF Preview
              </span>
              <button
                type="button"
                onClick={() => setPreviewPdfUrl(null)}
                className="flex size-10 sm:size-8 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] touch-manipulation shrink-0"
                aria-label="Close"
              >
                <HiOutlineX className="size-5" strokeWidth={2} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-2 sm:p-4">
              <Document
                file={previewPdfUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                loading={<p className="text-sm text-[#6d6d6d]" style={{ fontFamily: "Inter, sans-serif" }}>Loading PDF…</p>}
                error={<p className="text-sm text-red-600" style={{ fontFamily: "Inter, sans-serif" }}>Failed to load PDF.</p>}
                className="flex flex-col items-center gap-4"
              >
                {numPages != null &&
                  Array.from({ length: numPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={Math.min(800, typeof window !== "undefined" ? window.innerWidth - 24 : 800)}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            </div>
          </div>
        </div>
      )}

      {/* Hidden isRequired - keep for API; can add subtle UI later */}
      <input
        type="hidden"
        name="isRequired"
        value={isRequired ? "1" : "0"}
      />
    </form>
  );
}
